export const Stage = {
  IDENTIFIED: 'identified',
  QUALIFYING: 'qualifying',
  COMPLIANCE_REVIEW: 'compliance_review',
  PROPOSAL: 'proposal',
  ONBOARDING: 'onboarding',
  ACTIVE: 'active',
} as const;
export type Stage = (typeof Stage)[keyof typeof Stage];

export const STAGE_ORDER: Stage[] = [
  Stage.IDENTIFIED,
  Stage.QUALIFYING,
  Stage.COMPLIANCE_REVIEW,
  Stage.PROPOSAL,
  Stage.ONBOARDING,
  Stage.ACTIVE,
];

export const STAGE_DEFAULT_PROBABILITY: Record<Stage, number> = {
  [Stage.IDENTIFIED]: 10,
  [Stage.QUALIFYING]: 25,
  [Stage.COMPLIANCE_REVIEW]: 50,
  [Stage.PROPOSAL]: 70,
  [Stage.ONBOARDING]: 90,
  [Stage.ACTIVE]: 100,
};

export const Role = {
  ADMIN: 'admin',
  RM: 'rm',
  MANAGER: 'manager',
} as const;
export type Role = (typeof Role)[keyof typeof Role];

export const ActivityType = {
  VISIT: 'visit',
  CALL: 'call',
  EMAIL: 'email',
  MEETING: 'meeting',
  NOTE: 'note',
} as const;
export type ActivityType = (typeof ActivityType)[keyof typeof ActivityType];

export const KycStatus = {
  PENDING: 'pending',
  IN_PROGRESS: 'in_progress',
  COMPLETE: 'complete',
  EXPIRED: 'expired',
} as const;
export type KycStatus = (typeof KycStatus)[keyof typeof KycStatus];

export const OppSource = {
  RECOMMENDATION: 'recommendation',
  EVENT: 'event',
  INTERMEDIARY: 'intermediary',
  BANK: 'bank',
  LAWYER: 'lawyer',
  LINKEDIN: 'linkedin',
  OTHER: 'other',
} as const;
export type OppSource = (typeof OppSource)[keyof typeof OppSource];

export const Civility = {
  MR: 'mr',
  MRS: 'mrs',
  MS: 'ms',
  DR: 'dr',
  PROF: 'prof',
  OTHER: 'other',
} as const;
export type Civility = (typeof Civility)[keyof typeof Civility];

export const CIVILITY_LABEL: Record<Civility, string> = {
  mr: 'M.',
  mrs: 'Mme',
  ms: 'Mlle',
  dr: 'Dr.',
  prof: 'Pr.',
  other: '—',
};

export const WealthTranche = {
  UNDER_1M: 'under_1m',
  FROM_1M_TO_5M: 'from_1m_to_5m',
  FROM_5M_TO_25M: 'from_5m_to_25m',
  FROM_25M_TO_100M: 'from_25m_to_100m',
  OVER_100M: 'over_100m',
  UNKNOWN: 'unknown',
} as const;
export type WealthTranche = (typeof WealthTranche)[keyof typeof WealthTranche];

export const WEALTH_TRANCHE_LABEL: Record<WealthTranche, string> = {
  under_1m: '< 1 M CHF',
  from_1m_to_5m: '1 – 5 M CHF',
  from_5m_to_25m: '5 – 25 M CHF',
  from_25m_to_100m: '25 – 100 M CHF',
  over_100m: '> 100 M CHF',
  unknown: 'Non renseigné',
};

export const WealthSource = {
  INHERITANCE: 'inheritance',
  BUSINESS: 'business',
  FINANCE: 'finance',
  REAL_ESTATE: 'real_estate',
  PROFESSIONAL: 'professional',
  OTHER: 'other',
  UNKNOWN: 'unknown',
} as const;
export type WealthSource = (typeof WealthSource)[keyof typeof WealthSource];

export const WEALTH_SOURCE_LABEL: Record<WealthSource, string> = {
  inheritance: 'Héritage',
  business: 'Entreprise',
  finance: 'Finance',
  real_estate: 'Immobilier',
  professional: 'Activité professionnelle',
  other: 'Autre',
  unknown: 'Non renseigné',
};

/**
 * Derived contact status from its opportunities (CLAUDE.md prospect flow).
 * Not stored in DB; computed by the API at read time.
 */
export const ContactStatus = {
  PROSPECT: 'prospect',
  QUALIFIED_LEAD: 'qualified_lead',
  CLIENT: 'client',
} as const;
export type ContactStatus = (typeof ContactStatus)[keyof typeof ContactStatus];

export const CONTACT_STATUS_LABEL: Record<ContactStatus, string> = {
  prospect: 'Prospect',
  qualified_lead: 'Lead qualifié',
  client: 'Client',
};

export const AuditAction = {
  READ: 'read',
  CREATE: 'create',
  UPDATE: 'update',
  DELETE: 'delete',
  EXPORT: 'export',
  LOGIN: 'login',
  LOGOUT: 'logout',
  LOGIN_FAILED: 'login_failed',
} as const;
export type AuditAction = (typeof AuditAction)[keyof typeof AuditAction];

// ISO 3166 alpha-2 codes of EU member states — used for reverse solicitation MiFID II alert
export const EU_COUNTRIES: ReadonlySet<string> = new Set([
  'AT',
  'BE',
  'BG',
  'HR',
  'CY',
  'CZ',
  'DK',
  'EE',
  'FI',
  'FR',
  'DE',
  'GR',
  'HU',
  'IE',
  'IT',
  'LV',
  'LT',
  'LU',
  'MT',
  'NL',
  'PL',
  'PT',
  'RO',
  'SK',
  'SI',
  'ES',
  'SE',
]);

export function isEuDomiciled(country: string): boolean {
  // Accepts both strict ISO 3166-1 alpha-2 codes ("FR", "DE") and richer
  // labels imported from the legacy Excel ("UK RES NON DOM", "SWISS - Basel").
  // Strategy: trim the input and try the first 2 characters as an ISO code.
  // Returns false for anything we can't recognize — the EU/MiFID reverse-
  // solicitation alert is conservative on purpose.
  if (!country) return false;
  const upper = country.trim().toUpperCase();
  if (EU_COUNTRIES.has(upper)) return true;
  // Fallback: look at the first 2 chars only.
  return EU_COUNTRIES.has(upper.slice(0, 2));
}

// -----------------------------------------------------------------------------
// PIPELINE STATUS MAPPING (Excel legacy → CRM Stage)
// -----------------------------------------------------------------------------
/**
 * The reference Excel uses a freer vocabulary than the CRM's 6 canonical
 * stages. Mapping is applied at import time (seed script + table edits) so
 * historical data fits the pipeline without losing meaning.
 *
 * Anything not in this map keeps its raw value in the seed log for audit but
 * is downgraded to `identified` so the pipeline doesn't crash on unknown text.
 */
export const EXCEL_STATUS_TO_STAGE: Record<string, Stage> = {
  'Very Early': Stage.IDENTIFIED,
  Negociation: Stage.QUALIFYING,
  Négociation: Stage.QUALIFYING,
  'Internal compliance check': Stage.COMPLIANCE_REVIEW,
  'Compliance check': Stage.COMPLIANCE_REVIEW,
  Advanced: Stage.PROPOSAL,
  'Portfolio proposal': Stage.PROPOSAL,
  Agreed: Stage.PROPOSAL,
  'Account opened - Transfer in progress': Stage.ONBOARDING,
  'Onboarding in progress': Stage.ONBOARDING,
  'Portfolio Implemented': Stage.ACTIVE,
};

export function mapExcelStatusToStage(raw: string | null | undefined): Stage {
  if (!raw) return Stage.IDENTIFIED;
  const trimmed = raw.trim();
  return EXCEL_STATUS_TO_STAGE[trimmed] ?? Stage.IDENTIFIED;
}

export const STAGE_LABEL_FR: Record<Stage, string> = {
  [Stage.IDENTIFIED]: 'Identified',
  [Stage.QUALIFYING]: 'Qualifying',
  [Stage.COMPLIANCE_REVIEW]: 'Compliance Review',
  [Stage.PROPOSAL]: 'Proposal',
  [Stage.ONBOARDING]: 'Onboarding',
  [Stage.ACTIVE]: 'Active',
};

// Branded chip palette for the pipeline table (E9 spec).
export const STAGE_CHIP: Record<Stage, { bg: string; fg: string }> = {
  [Stage.IDENTIFIED]: { bg: '#E3E5E6', fg: '#1A2832' },
  [Stage.QUALIFYING]: { bg: '#EEF4F8', fg: '#1A2832' },
  [Stage.COMPLIANCE_REVIEW]: { bg: '#FDECEB', fg: '#A8401E' },
  [Stage.PROPOSAL]: { bg: '#FFF7E0', fg: '#8B6914' },
  [Stage.ONBOARDING]: { bg: '#E8F3EE', fg: '#2E7D5B' },
  [Stage.ACTIVE]: { bg: '#1A2832', fg: '#FFFFFF' },
};

// -----------------------------------------------------------------------------
// MANAGER VIEW — soft pseudonym for opportunity rows
// -----------------------------------------------------------------------------
/**
 * Produce a short, human-readable initials code from a person name pair.
 *   "Alpha", "Bravo"            → "BRA-ALP"
 *   "Mr M.H", null              → "M.H"
 *   null,    null               → "ANON"
 *
 * SECURITY NOTE: this is a *soft* pseudonym for at-a-glance reading of the
 * manager view. It is NOT a cryptographic identifier (any leaked full name
 * makes the mapping trivially reversible). For audit-grade pseudonymisation
 * see apps/api/src/lib/pseudonymize.ts (HMAC-SHA-256 backed).
 *
 * The manager view exists so the manager can spot patterns (by sector, by
 * geography, by RM) without seeing client identities. Combined with the
 * existing regulatory pseudonym, the leak surface stays bounded.
 */
export function softInitials(
  firstName: string | null | undefined,
  lastName: string | null | undefined,
): string {
  const f = (firstName ?? '').replace(/[^A-Za-zÀ-ÿ]/g, '').toUpperCase();
  const l = (lastName ?? '').replace(/[^A-Za-zÀ-ÿ]/g, '').toUpperCase();
  if (!f && !l) return 'ANON';
  if (!f) return l.slice(0, 3) || 'ANON';
  if (!l) return f.slice(0, 3) || 'ANON';
  return `${l.slice(0, 3)}-${f.slice(0, 3)}`;
}

/**
 * Apply the soft pseudonymisation to a free-form opportunity name when no
 * structured contact is attached. Splits on the first space and treats the
 * tokens as first/last. Strips honorific prefixes ("Mr", "Mrs", "Dr.", …).
 */
export function softInitialsFromFullName(rawName: string | null | undefined): string {
  if (!rawName) return 'ANON';
  // Strip the obvious "Mr ", "Mrs ", "Dr.", "Mme " prefixes so they don't
  // become the "first name" portion of the code.
  const stripped = rawName.replace(/^(Mr|Mrs|Ms|Mlle|Dr|Pr|Prof|Sir|Mme|M\.)\b\.?/i, '').trim();
  const tokens = stripped.split(/\s+/).filter(Boolean);
  if (tokens.length === 0) return 'ANON';
  if (tokens.length === 1) return softInitials(null, tokens[0]);
  return softInitials(tokens[0], tokens.slice(1).join(' '));
}

// -----------------------------------------------------------------------------
// PIPELINE ROW FORMULAS (CRM equivalent of Excel columns H and K)
// -----------------------------------------------------------------------------
/**
 * Estimated RoA = initial_expected × (roa_bps / 10_000)
 * Returns null when any input is missing so the UI can render a blank cell
 * (per spec: never "0", "-" or "N/A").
 */
export function computeEstimatedRoaChf(
  initialExpectedAmount: number | null | undefined,
  roaBps: number | null | undefined,
): number | null {
  if (
    initialExpectedAmount == null ||
    !Number.isFinite(initialExpectedAmount) ||
    initialExpectedAmount === 0 ||
    roaBps == null ||
    !Number.isFinite(roaBps)
  ) {
    return null;
  }
  return initialExpectedAmount * (roaBps / 10_000);
}

/**
 * Expected RoA = potential × (roa_bps / 10_000)
 * `potential` may be either a number or a text marker ("Negociation").
 * Text markers always produce a null result (blank cell).
 */
export function computeExpectedRoaChf(
  potential: number | string | null | undefined,
  roaBps: number | null | undefined,
): number | null {
  if (typeof potential !== 'number' || !Number.isFinite(potential) || potential === 0) {
    return null;
  }
  if (roaBps == null || !Number.isFinite(roaBps)) return null;
  return potential * (roaBps / 10_000);
}
